import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ShieldCheck, X } from 'lucide-react';
import { useStore } from '../../src/lib/store';
import { effectiveConsent, isGpcEnabled } from '../../src/lib/consent';

const Toggle = ({ checked, disabled, onChange, label }: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-emerald-500' : 'bg-white/15'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

export const PrivacyBanner = () => {
  const { t } = useTranslation();
  const { cookieConsent, setCookieConsent, isConsentPanelOpen, setConsentPanelOpen, consentOptInRequired } = useStore();
  const gpc = isGpcEnabled();
  const decided = !!cookieConsent.decidedAt;
  const visible = !decided || isConsentPanelOpen;
  // Outside opt-in regions Analytics is on until the visitor turns it off (src/lib/consent.ts).
  const analyticsNow = effectiveConsent(cookieConsent, consentOptInRequired).analytics;

  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(analyticsNow);
  const [advertising, setAdvertising] = useState(cookieConsent.advertising && !gpc);

  // Reopening from the footer goes straight to the per-category choices,
  // starting from what the visitor picked last time.
  useEffect(() => {
    if (!isConsentPanelOpen) return;
    setAnalytics(analyticsNow);
    setAdvertising(cookieConsent.advertising && !gpc);
    setCustomizing(true);
  }, [isConsentPanelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // The policy loads after the first render; keep an untouched toggle in step with it.
  useEffect(() => {
    if (!customizing) setAnalytics(analyticsNow);
  }, [analyticsNow]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (choice: { analytics: boolean; advertising: boolean }) => {
    setCookieConsent({ analytics: choice.analytics, advertising: choice.advertising && !gpc });
    setCustomizing(false);
  };

  const buttonBase = 'w-full sm:w-auto px-5 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest active:scale-95 transition-all whitespace-nowrap';
  // Reject and Accept share one style so neither is the "easy" option.
  const choiceButton = `${buttonBase} bg-emerald-500 text-white hover:bg-emerald-600`;
  const secondaryButton = `${buttonBase} bg-white/10 text-white hover:bg-white/15`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-consent"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="fixed bottom-6 left-0 right-0 w-full px-4 flex justify-center z-[1000] pointer-events-none"
        >
          <div
            role="dialog"
            aria-label={t('cookie_consent.title', { defaultValue: 'Cookie choices' })}
            className="bg-[#1a2b3c]/95 backdrop-blur-xl border border-white/10 p-4 sm:p-5 rounded-[2rem] shadow-2xl w-full max-w-4xl pointer-events-auto text-white max-h-[85vh] overflow-y-auto"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-500 flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} />
                </div>
                <p className="text-[10px] sm:text-xs font-bold leading-relaxed text-white/70 max-w-xl">
                  <Trans
                    i18nKey={consentOptInRequired ? 'cookie_consent.text' : 'cookie_consent.text_opt_out'}
                    defaults={consentOptInRequired
                      ? 'We use necessary cookies to run CartLens. With your permission we also use analytics cookies to improve it and advertising cookies to measure our ads. You can change this anytime. See our <link>Privacy Policy</link>.'
                      : 'We use necessary cookies to run CartLens and analytics cookies to improve it. You can turn analytics off with Reject all or Customize. Advertising cookies stay off unless you allow them. See our <link>Privacy Policy</link>.'}
                    components={{
                      link: <Link to="/privacy" className="text-emerald-400 hover:underline hover:text-emerald-300 transition-colors" />,
                    }}
                  />
                </p>
              </div>
              {decided && (
                <button
                  type="button"
                  onClick={() => { setConsentPanelOpen(false); setCustomizing(false); }}
                  aria-label={t('cookie_consent.close', { defaultValue: 'Close' })}
                  className="self-end sm:self-center p-2 rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            <AnimatePresence initial={false}>
              {customizing && (
                <motion.div
                  key="categories"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 flex flex-col gap-2">
                    {[
                      {
                        id: 'necessary',
                        title: t('cookie_consent.necessary_title', { defaultValue: 'Necessary' }),
                        body: t('cookie_consent.necessary_body', { defaultValue: 'Sign-in, security, your settings and this choice. Always on.' }),
                        checked: true,
                        disabled: true,
                      },
                      {
                        id: 'analytics',
                        title: t('cookie_consent.analytics_title', { defaultValue: 'Analytics' }),
                        body: t('cookie_consent.analytics_body', { defaultValue: 'Helps us understand how CartLens is used so we can improve it and fix problems.' }),
                        checked: analytics,
                        onChange: setAnalytics,
                      },
                      {
                        id: 'advertising',
                        title: t('cookie_consent.advertising_title', { defaultValue: 'Advertising' }),
                        body: t('cookie_consent.advertising_body', { defaultValue: 'Helps us measure our ads. Turning this off opts you out of the sale or sharing of your personal information.' }),
                        checked: advertising,
                        disabled: gpc,
                        onChange: setAdvertising,
                        note: gpc ? t('cookie_consent.gpc_note', { defaultValue: 'Your browser sends a Global Privacy Control signal, so advertising cookies stay off.' }) : null,
                      },
                    ].map(row => (
                      <div key={row.id} className="flex items-center justify-between gap-4 bg-white/5 border border-white/5 rounded-2xl px-4 py-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-widest">{row.title}</p>
                          <p className="text-[11px] text-white/60 leading-relaxed mt-1">{row.body}</p>
                          {row.note && <p className="text-[11px] text-emerald-400 leading-relaxed mt-1">{row.note}</p>}
                        </div>
                        <Toggle checked={row.checked} disabled={row.disabled} onChange={row.onChange} label={row.title} />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2">
              {customizing ? (
                <button type="button" onClick={() => choose({ analytics, advertising })} className={secondaryButton}>
                  {t('cookie_consent.save', { defaultValue: 'Save choices' })}
                </button>
              ) : (
                <button type="button" onClick={() => setCustomizing(true)} className={secondaryButton}>
                  {t('cookie_consent.customize', { defaultValue: 'Customize' })}
                </button>
              )}
              <button type="button" onClick={() => choose({ analytics: false, advertising: false })} className={choiceButton}>
                {t('cookie_consent.reject_all', { defaultValue: 'Reject all' })}
              </button>
              <button type="button" onClick={() => choose({ analytics: true, advertising: true })} className={choiceButton}>
                {t('cookie_consent.accept_all', { defaultValue: 'Accept all' })}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
